import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { TApi, TTicketType, TicketStatusOptions } from '../../../../shared/const'
import http from '../../../../shared/http'
import { errorText } from '../../../../shared/errorMessage'
import useAuthImages from '../../../../shared/useAuthImages'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import { askDiscardChanges } from '../../../../shared/confirmDialog'

import Btn from '../../../../UI/Btn'
import { PlainInput } from '../../../../UI/PlainField'
import { RichTextEditor } from '../../../../UI/RichText'

import AttachmentDropzone from './AttachmentDropzone'
import { pruefeAnhaenge } from './AttachmentPicker'
import TicketFields from './TicketFields'
import laufzeitText from './projectRuntime'
import styles from './detail.module.scss'
import formStyles from './form.module.scss'
import attachStyles from './attachments.module.scss'

const formatSize = bytes => `${(bytes / 1024 / 1024).toFixed(1)} MB`

const kurzTyp = type => {
	if (type.startsWith('image/')) return 'BILD'
	if (type === 'application/pdf') return 'PDF'
	if (type.startsWith('video/')) return 'VIDEO'
	return 'DATEI'
}

const TABS = [
	{ key: 'anforderung', label: 'Anforderung' },
	{ key: 'anhaenge', label: 'Anhänge & Screenshots' }
]

/**
 * Anlegen und Bearbeiten einer Anforderung.
 *
 * Aufbau und Stil sind dieselben wie beim Öffnen einer Anforderung
 * (`TicketDetail`): gleicher Rahmen, gleiche Größe und Stelle, gleiche Reiter.
 *
 * Die Reiter trennen zwei Fragen, die nichts miteinander zu tun haben:
 * „Anforderung" ist der Inhalt (worum geht es, wie heißt es, was ist zu tun),
 * „Einstufung" die Einordnung (Status, Zuständigkeit, Dringlichkeit, Projekt).
 * Die Art steht in beiden — im ersten, weil sie die Beschreibung prägt, im
 * zweiten, weil sie zur Einordnung gehört. Doppelt, aber an beiden Stellen
 * richtig: Man soll den Reiter nicht wechseln müssen, um zu sehen, ob man
 * gerade einen Fehler oder eine Entscheidung beschreibt.
 *
 * Anhänge lassen sich auch beim Anlegen hinzufügen, obwohl die Anforderung noch
 * gar nicht existiert. Die Dateien werden dafür zunächst gesammelt und erst
 * nach dem Speichern hochgeladen. Ohne das müsste man zweimal durch: erst
 * anlegen, dann öffnen, dann bebildern.
 *
 * Schlägt ein einzelner Anhang fehl, bleibt die Anforderung trotzdem
 * gespeichert. Sie deswegen wieder zu verwerfen wäre schlechter — der
 * geschriebene Text ist mehr wert als das Bild.
 */
export default function TicketFormModal({
	ticket,
	projects,
	assignees,
	onClose,
	onSaved
}) {
	const isNew = !ticket?.id

	const ausgangswerte = useMemo(
		() => ({
			title: ticket?.title || '',
			description: ticket?.description || '',
			// „Fehler" als Startwert: Es ist die haeufigste Art (35 von 80), und
			// wer etwas Kaputtes meldet, tippt den Titel und speichert — wer einen
			// Wunsch oder eine Entscheidung erfasst, denkt ohnehin ueber die
			// Einordnung nach.
			type: ticket?.type || TTicketType.ISSUE,
			status: ticket?.status || TicketStatusOptions[0].value,
			urgency: ticket?.urgency ?? 2,
			importance: ticket?.importance ?? 2,
			projectId: ticket?.projectId ?? null,
			assignedTo: ticket?.assignedTo?.id ?? ticket?.assignedToId ?? null
		}),
		[ticket]
	)

	const [form, setForm] = useState(ausgangswerte)
	const [activeTab, setActiveTab] = useState('anforderung')
	const [queue, setQueue] = useState([])
	const [rejected, setRejected] = useState([])
	const [isSaving, setSaving] = useState(false)
	// Zwei Meldungen mit zwei Orten: Der fehlende Titel gehoert ans Feld, ein
	// gescheitertes Speichern ueber die Fusszeile. Beides in einem Zustand hiess
	// bisher, dass derselbe Satz zweimal auf dem Bildschirm stand.
	const [titleError, setTitleError] = useState('')
	const [error, setError] = useState('')

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(activeTab, tabsRef)

	/*
	 * Fokus ins Titelfeld, ohne den Inhalt zu verschieben.
	 *
	 * Das Attribut `autoFocus` lässt den Browser das Feld in den Blick scrollen
	 * — und schob dabei die Zeile mit Art und Score halb unter die Kopfleiste,
	 * obwohl das Feld ohnehin sichtbar war. `preventScroll` gibt es nur beim
	 * Aufruf von focus(), nicht als Attribut.
	 */
	const titelRef = useRef(null)
	useEffect(() => {
		titelRef.current?.focus({ preventScroll: true })
	}, [])

	const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

	const score = form.urgency * form.importance

	/*
	 * Gibt es etwas zu verlieren?
	 *
	 * Vorgemerkte Anhänge zählen mit: Sie sind noch nirgends gespeichert und
	 * wären beim Schließen weg — für die Person, die gerade fünf Bilder
	 * eingefügt hat, der ärgerlichste Verlust von allen.
	 */
	const geaendert =
		queue.length > 0 ||
		Object.keys(ausgangswerte).some(key => form[key] !== ausgangswerte[key])

	/*
	 * Schließen mit Rückfrage. Ein Klick daneben schließt weiterhin sofort,
	 * solange nichts geändert wurde — die Rückfrage soll den schnellen Blick
	 * ins Fenster nicht ausbremsen.
	 */
	const schliessen = async () => {
		if (isSaving) return
		if (geaendert && !(await askDiscardChanges())) return

		onClose()
	}

	const backdropProps = useDialogDismiss(schliessen, { enabled: !isSaving })

	// Beim Bearbeiten bereits vorhandene Anhänge. Sie lassen sich hier nicht
	// ändern, sollen aber sichtbar sein — sonst wirkt der Reiter leer, obwohl
	// Bilder da sind.
	const vorhandene = useMemo(() => (isNew ? [] : ticket?.screenshots || []), [isNew, ticket])
	const sources = useMemo(
		() =>
			vorhandene.map(shot => ({
				id: shot.id,
				url: TApi.ADMIN_TICKET_SCREENSHOT_FILE(ticket.id, shot.id)
			})),
		[vorhandene, ticket]
	)
	const imageUrls = useAuthImages(sources)

	const projekt = projects.find(entry => entry.id === form.projectId)

	const addFiles = (files, abgelehnt) => {
		// Datei und ihr Satz wandern zusammen durch die Warteschlange — getrennt
		// gefuehrt liefen die beiden Listen beim Entfernen auseinander.
		setQueue(prev => [...prev, ...files.map(file => ({ file, description: '' }))])
		setRejected(abgelehnt)
	}

	const uploadQueue = async ticketId => {
		// Nacheinander: Die Reihenfolge entsteht am Server aus der Position des
		// zuletzt gespeicherten Anhangs. Parallel gestartet laesen sich zwei
		// Uploads denselben Wert und landen auf gleicher Stelle.
		let fehler = 0
		for (const { file, description } of queue) {
			const body = new FormData()
			body.append('screenshot', file, file.name || 'anhang')
			if (description?.trim()) body.append('description', description.trim())
			// eslint-disable-next-line no-await-in-loop
			// `disableToast`: Über gescheiterte Anhänge wird unten gesammelt berichtet.
			await http
				.post(TApi.ADMIN_TICKET_SCREENSHOTS(ticketId), body, { disableToast: true })
				.catch(() => {
					fehler += 1
				})
		}
		if (fehler) {
			toast.warn(
				`${fehler} von ${queue.length} Anhängen konnten nicht gespeichert werden. Die Anforderung ist gespeichert.`
			)
		}
	}

	const submit = event => {
		event.preventDefault()

		if (form.title.trim().length < 3) {
			setTitleError('Bitte einen Titel mit mindestens drei Zeichen angeben.')
			// Der Titel steht im ersten Reiter. Bleibt die Meldung dort stehen, wo
			// das Feld nicht zu sehen ist, sucht man vergeblich.
			setActiveTab('anforderung')
			return
		}

		setTitleError('')
		setError('')
		setSaving(true)

		const payload = {
			title: form.title.trim(),
			description: form.description.trim() || null,
			type: form.type,
			status: form.status,
			urgency: Number(form.urgency),
			importance: Number(form.importance),
			// Leere Auswahl heisst "keinem Projekt zugeordnet" bzw. "niemandem
			// zugewiesen", nicht "0".
			projectId: form.projectId ?? null,
			assignedTo: form.assignedTo ?? null
		}

		// `disableToast`: Der Grund steht im Formular, nicht zusätzlich als Meldung.
		const request = isNew
			? http.post(TApi.ADMIN_TICKETS, payload, { disableToast: true })
			: http.put(TApi.ADMIN_TICKET_ID(ticket.id), payload, { disableToast: true })

		request
			.then(async ({ data }) => {
				if (queue.length) await uploadQueue(data.id)
				toast.success(isNew ? 'Anforderung angelegt.' : 'Anforderung gespeichert.')
				onSaved()
			})
			.catch(err => setError(errorText(err, 'Speichern fehlgeschlagen.')))
			.finally(() => setSaving(false))
	}

	const tabCount = key => (key === 'anhaenge' ? vorhandene.length + queue.length : null)

	return (
		<div
			className={styles.backdrop}
			{...backdropProps}
			// Einfuegen aus der Zwischenablage liegt auf dem ganzen Dialog. In
			// einem Textfeld greift der Browser zuerst zu - dort wird Text
			// eingefuegt, Dateien landen weiterhin hier.
			onPaste={event => {
				const files = Array.from(event.clipboardData?.files || [])
				if (files.length) addFiles(...pruefeAnhaenge(files))
			}}
		>
			<form className={styles.modal} onSubmit={submit}>
				<header className={styles.head}>
					<div className={styles.headLeft}>
						<span className={styles.position}>
							{isNew ? 'Neue Anforderung' : `Anforderung #${ticket.id}`}
						</span>
						{/*
						 * Der Titel läuft beim Tippen mit — so sieht man, wie die
						 * Anforderung später in der Liste steht. Bei Überlänge
						 * gekürzt; der ganze Text steht im Feld darunter und im
						 * Tooltip.
						 */}
						{Boolean(form.title.trim()) && (
							<span className={styles.headTitle} title={form.title.trim()}>
								{form.title.trim()}
							</span>
						)}
					</div>
					<button
						type="button"
						className={styles.close}
						onClick={schliessen}
						aria-label="Schließen"
					>
						×
					</button>
				</header>

				<div className={styles.split}>
				<div className={styles.body}>
					<div className={styles.tabs} role="tablist" ref={tabsRef}>
						{TABS.map(tab => {
							const count = tabCount(tab.key)
							return (
								<button
									key={tab.key}
									type="button"
									role="tab"
									aria-selected={activeTab === tab.key}
									className={clsx(styles.tab, activeTab === tab.key && styles.tabActive)}
									onClick={() => setActiveTab(tab.key)}
								>
									{tab.label}
									{count !== null && <span className={styles.tabCount}>{count}</span>}
								</button>
							)
						})}
					</div>

					<div className={styles.panel}>
						{activeTab === 'anforderung' && (
							<>
								<PlainInput
									label="Titel"
									inputRef={titelRef}
									value={form.title}
									maxLength={255}
									error={titleError || undefined}
									placeholder="Kurz und konkret, z. B. „Rechnung lässt sich nicht öffnen“"
									onChange={event => {
										set('title', event.target.value)
										if (titleError) setTitleError('')
									}}
								/>

								{/*
								 * Mit den vier Grundauszeichnungen: Eine Beschreibung
								 * mit drei Aufzählungspunkten ist genau das, was hier
								 * ständig geschrieben wird — bisher mit Bindestrichen
								 * und Sternchen, die als Bindestriche und Sternchen
								 * dastanden.
								 */}
								<label className={formStyles.descriptionField}>
									<span className={formStyles.descriptionLabel}>Beschreibung</span>
									<RichTextEditor
										value={form.description}
										disabled={isSaving}
										ariaLabel="Beschreibung"
										placeholder="Was passiert, was sollte stattdessen passieren, und wie lässt es sich nachstellen?"
										onChange={wert => set('description', wert)}
									/>
								</label>
							</>
						)}

						{activeTab === 'anhaenge' && (
							<>
								{Boolean(vorhandene.length) && (
									<div className={styles.thumbs}>
										{vorhandene.map((shot, index) => (
											<figure key={shot.id} className={styles.thumb}>
												{imageUrls[shot.id] ? (
													<img
														className={styles.thumbImg}
														src={imageUrls[shot.id]}
														alt={shot.caption || `Anhang ${index + 1}`}
													/>
												) : (
													<div className={styles.thumbLoading}>lädt …</div>
												)}
												<figcaption className={styles.thumbCaption}>
													<span className={styles.thumbName}>
														{shot.caption || `Bild ${index + 1}`}
													</span>
												</figcaption>
											</figure>
										))}
									</div>
								)}

								{Boolean(queue.length) && (
									<div className={attachStyles.queue}>
										{queue.map(({ file, description }, index) => (
											<div key={`${file.name}-${index}`} className={attachStyles.queueItem}>
												{file.type.startsWith('image/') ? (
													<img
														className={attachStyles.queueThumb}
														src={URL.createObjectURL(file)}
														alt=""
														// Die lokale Adresse wieder freigeben, sobald das
														// Bild geladen ist - sonst sammelt sie sich an.
														onLoad={event => URL.revokeObjectURL(event.target.src)}
													/>
												) : (
													<span className={attachStyles.queueIcon}>
														{kurzTyp(file.type)}
													</span>
												)}
												<div className={attachStyles.queueBody}>
													<span className={attachStyles.queueName}>
														{file.name || 'Anhang'}
														<span className={attachStyles.queueSize}>
															{formatSize(file.size)}
														</span>
													</span>
													{/*
													  * Derselbe Satz wie am Kommentarfeld: Was ist das,
													  * was ist darauf zu sehen? Gerade beim Anlegen
													  * hängen hier die Bildschirmfotos, die den Fehler
													  * zeigen — ohne Erklärung sind sie später stumm.
													  */}
													<input
														type="text"
														className={attachStyles.queueDescription}
														value={description}
														maxLength={2000}
														placeholder="Was ist darauf zu sehen? (hilft beim späteren Nachlesen)"
														aria-label={`Beschreibung zu ${file.name || 'Anhang'}`}
														onChange={event => {
															/*
															 * Den Wert vor dem Aktualisieren herausnehmen.
															 * React 17 recycelt das Ereignisobjekt: Wird
															 * `event.target.value` erst im Aktualisierer
															 * gelesen — der laeuft spaeter —, ist dort
															 * eine leere Zeichenkette. Der Satz kam damit
															 * nie an, ohne dass irgendetwas fehlschlug.
															 */
															const wert = event.target.value
															setQueue(prev =>
																prev.map((eintrag, i) =>
																	i === index ? { ...eintrag, description: wert } : eintrag
																)
															)
														}}
													/>
												</div>
												<button
													type="button"
													className={attachStyles.queueRemove}
													title="Entfernen"
													onClick={() =>
														setQueue(prev => prev.filter((_, i) => i !== index))
													}
												>
													×
												</button>
											</div>
										))}
									</div>
								)}

								<AttachmentDropzone onFiles={addFiles} disabled={isSaving} />

								{Boolean(rejected.length) && (
									<ul className={styles.rejected}>
										{rejected.map(grund => (
											<li key={grund}>{grund}</li>
										))}
									</ul>
								)}
							</>
						)}
					</div>

					{error && <p className={formStyles.error}>{error}</p>}
				</div>

				{/*
				 * Dieselbe Seitenspalte wie beim Öffnen einer Anforderung: alle sechs
				 * Angaben der Einordnung, immer sichtbar. Beide Fenster gleich
				 * aufzubauen ist hier kein Selbstzweck — wer eine Anforderung anlegt
				 * und sie danach öffnet, sah sonst zwei verschiedene Formulare.
				 */}
				<aside className={styles.sidebar}>
					<h3 className={styles.sidebarTitle}>Einordnung</h3>

					<TicketFields
						spalte
						values={form}
						projects={projects}
						assignees={assignees}
						disabled={isSaving}
						onChange={set}
					/>

					<div className={styles.sidebarMeta}>
						<span className={styles.scoreChip} title="Dringlichkeit × Wichtigkeit">
							Score {score}
						</span>
						{laufzeitText(projekt) && (
							<span className={styles.meta}>Laufzeit {laufzeitText(projekt)}</span>
						)}
					</div>
				</aside>
				</div>

				<footer className={styles.foot}>
					<span className={styles.meta}>
						{geaendert
							? 'Ungespeicherte Änderungen.'
							: 'Titel und Art genügen — alles Weitere lässt sich später ergänzen.'}
					</span>
					<div className={styles.footActions}>
						<Btn type="button" size="small" theme="secondary" onClick={schliessen}>
							Abbrechen
						</Btn>
						<Btn type="submit" size="small" theme="primary" disabled={isSaving}>
							{isSaving ? 'Wird gespeichert …' : 'Speichern'}
						</Btn>
					</div>
				</footer>
			</form>
		</div>
	)
}

TicketFormModal.propTypes = {
	ticket: PropTypes.object,
	projects: PropTypes.array,
	assignees: PropTypes.array,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired
}

TicketFormModal.defaultProps = {
	ticket: {},
	projects: [],
	assignees: []
}
